import { z } from 'zod';

export const newLayoutVisualizationTypes = ['swimlane', 'timeline', 'table'] as const;
export const newLayoutVisualizationTypeSchema = z.enum(newLayoutVisualizationTypes);

export const newDateHeaderTypes = [
  'startDate:week',
  'startDate:month',
  'startDate:quarter',
  'startDate:half',
  'startDate:year',
  'endDate:week',
  'endDate:month',
  'endDate:quarter',
  'endDate:half',
  'endDate:year',
] as const;

export const newHeaderDirections = ['horizontal', 'vertical'] as const;
export const newHeaderDirectionSchema = z.enum(newHeaderDirections);

export const newThemes = ['standard', 'compact'] as const;
export const newThemeSchema = z.enum(newThemes);

export const newHeaderTypes = ['status', 'parentItem', 'team', 'createdBy'] as const;
export const newAllHeaderTypes = [...newHeaderTypes, ...newDateHeaderTypes] as const;
export const newHeaderLabelsType = [...newHeaderTypes, 'itemDate'] as const;

export type NewHeaderLabelsType = (typeof newHeaderLabelsType)[number];
export type NewHeaderType = (typeof newHeaderLabelsType)[number];

export const NEW_HEADER_TYPE_TRANSLATIONS = {
  status: 'roadmapping.layout.headers.fields.status',
  parentItem: 'roadmapping.layout.headers.fields.parentItem',
  team: 'roadmapping.layout.headers.fields.team',
  itemDate: 'roadmapping.layout.headers.fields.date',
  createdBy: 'roadmapping.layout.headers.fields.createdBy',
} as const satisfies Record<NewHeaderLabelsType, string>;

export const newAllowedHeaderCustomFieldTypes = ['PICKLIST', 'MULTI_PICKLIST'] as const;
export type NewAllowedCustomFieldType = (typeof newAllowedHeaderCustomFieldTypes)[number];

export const newLayoutHeaderSchema = z.string().refine(
  (val) => {
    if (newAllHeaderTypes.includes(val as (typeof newAllHeaderTypes)[number])) {
      return true;
    }
    return /^[1-9]\d*$/.test(val) ? Number.parseInt(val, 10) <= Number.MAX_SAFE_INTEGER : false;
  },
  {
    error: `Invalid header specified. Header must be one of ['${newAllHeaderTypes.join("', '")}'] or a custom field id.`,
  },
);

export const newLayoutHeaderDTOSchema = z.nullable(newLayoutHeaderSchema);
export const newLayoutHeaderUpsertDTOSchema = z.optional(newLayoutHeaderDTOSchema);

export const newColorConfigSchema = z
  .object({
    mode: z.enum(['baseColor', 'palette']),
    baseColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    paletteId: z.string().optional(),
    colorByField: z.string().nullable().optional(),
    itemColorMap: z.record(z.string(), z.number()).optional(),
    matchHeaderColors: z.boolean().optional(),
    headerColorOrder: z.array(z.string()).optional(),
    desaturatedHeaderColorIndex: z.number().min(0).max(2).optional(),
  })
  .nullable()
  .optional();

export type NewColorConfig = z.infer<typeof newColorConfigSchema>;

export const newBaseLayoutDTOSchema = z.object({
  itemLevelId: z.number(),
  visualizationId: z.number().int().positive(),
  theme: newThemeSchema,
});

export const newCanvasBaseLayoutDTOSchema = newBaseLayoutDTOSchema.extend({
  // Allow for header1 and header2 to be null
  header1: newLayoutHeaderDTOSchema,
  header2: newLayoutHeaderDTOSchema,
  headerDirection: newHeaderDirectionSchema,
  showEmptyHeaders: z.boolean().default(false),

  colorConfig: newColorConfigSchema,

  // Label by headers for card labels
  labelByHeader1: newLayoutHeaderDTOSchema,
  labelByHeader2: newLayoutHeaderDTOSchema,

  // Custom header ordering - array of header IDs in desired order
  columnHeaderOrder: z.array(z.string()).optional(),
  rowHeaderOrder: z.array(z.string()).optional(),
});

export const newSwimlaneLayoutDTOSchema = z.object({
  ...newCanvasBaseLayoutDTOSchema.shape,
  visualizationType: z.literal('swimlane'),
});

export type NewSwimlaneLayoutDTO = z.infer<typeof newSwimlaneLayoutDTOSchema>;

export const newTimelineLayoutDTOSchema = z.object({
  ...newCanvasBaseLayoutDTOSchema.shape,
  visualizationType: z.literal('timeline'),
});

export type NewTimelineLayoutDTO = z.infer<typeof newTimelineLayoutDTOSchema>;

// Item fields that can be used as table columns (from item-dto)
export const TABLE_COLUMN_ITEM_FIELDS = [
  'name',
  'externalKey',
  'priority',
  'timeSpent',
  'targetStartDate',
  'targetFinishDate',
  'doneDate',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'status',
  'team',
  'estimation',
] as const;
type ItemField = (typeof TABLE_COLUMN_ITEM_FIELDS)[number];

// Schedule fields that can be used as table columns
export const TABLE_COLUMN_SCHEDULE_FIELDS = [
  'planId',
  'expectedStartDate',
  'expectedFinishDate',
  'latestFinishDate',
] as const;

type ScheduleField = (typeof TABLE_COLUMN_SCHEDULE_FIELDS)[number];

export type TableColumnSystemField = ItemField | ScheduleField;
export const TABLE_COLUMN_SYSTEM_FIELDS = [
  ...TABLE_COLUMN_ITEM_FIELDS,
  ...TABLE_COLUMN_SCHEDULE_FIELDS,
] as const satisfies readonly TableColumnSystemField[];

export type TableColumnTranslationKey =
  `views.table.columns.systemFields.${TableColumnSystemField}`;
export const TABLE_COLUMN_SYSTEM_FIELDS_TRANSLATIONS: ReadonlyMap<
  TableColumnSystemField,
  TableColumnTranslationKey
> = new Map(
  TABLE_COLUMN_SYSTEM_FIELDS.map((field) => [
    field,
    `views.table.columns.systemFields.${field}` as TableColumnTranslationKey,
  ]),
);

export const REQUIRED_TABLE_COLUMN_FIELDS: ReadonlySet<TableColumnSystemField> = new Set(['name']);

export const tableColumnIdentifierSchema = z.object({
  type: z.enum([...TABLE_COLUMN_SYSTEM_FIELDS, 'customField'] as unknown as [string, ...string[]]),
  customFieldId: z.number().nullish(),
});
export type TableColumnIdentifier = z.infer<typeof tableColumnIdentifierSchema>;

export const newTableColumnDTOSchema = tableColumnIdentifierSchema.extend({
  width: z.number().optional(),
});
export type NewTableColumnDTO = z.infer<typeof newTableColumnDTOSchema>;

export const SortSchema = tableColumnIdentifierSchema.extend({
  order: z.enum(['asc', 'desc']),
  collate: z.boolean().optional(),
});
export type SortDto = z.infer<typeof SortSchema>;

export const newTableLayoutDTOSchema = newBaseLayoutDTOSchema.extend({
  visualizationType: z.literal('table'),
  columns: z.array(newTableColumnDTOSchema),
  sort: SortSchema.optional(),
});

export type NewTableLayoutDTO = z.infer<typeof newTableLayoutDTOSchema>;

export const DEFAULT_TABLE_COLUMN_WIDTH = 150;

/**
 * Default columns for new table layouts.
 * Shared between client and server to ensure consistency.
 */
export const DEFAULT_TABLE_COLUMNS: NewTableColumnDTO[] = [
  { type: 'name', customFieldId: null },
  { type: 'externalKey', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'estimation', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'status', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'team', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'targetStartDate', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'targetFinishDate', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
];

export const newLayoutDTOSchema = z.discriminatedUnion('visualizationType', [
  newSwimlaneLayoutDTOSchema,
  newTimelineLayoutDTOSchema,
  newTableLayoutDTOSchema,
]);

export type NewLayoutDTO = z.infer<typeof newLayoutDTOSchema>;

export const newBaseLayoutUpsertDTOSchema = z.object({
  visualizationType: newLayoutVisualizationTypeSchema,
  itemLevelId: z.number().int().positive(),
  theme: newThemeSchema.optional(),
});

export const newCanvasLayoutUpsertDTOSchema = newBaseLayoutUpsertDTOSchema.extend({
  header1: z.optional(newLayoutHeaderDTOSchema),
  header2: z.optional(newLayoutHeaderDTOSchema),
  headerDirection: newHeaderDirectionSchema.optional(),
  showEmptyHeaders: z.boolean().optional(),
  colorConfig: newColorConfigSchema,
  labelByHeader1: z.optional(newLayoutHeaderDTOSchema),
  labelByHeader2: z.optional(newLayoutHeaderDTOSchema),
});

export type NewCanvasLayoutUpsertDTO = z.infer<typeof newCanvasLayoutUpsertDTOSchema>;

export const newUpsertSwimlaneLayoutDTOSchema = z.object({
  ...newCanvasLayoutUpsertDTOSchema.shape,
  visualizationType: z.literal('swimlane'),
});

export type NewSwimlaneLayoutUpsertDTO = z.infer<typeof newUpsertSwimlaneLayoutDTOSchema>;

export const newUpsertTimelineLayoutDTOSchema = z.object({
  ...newCanvasLayoutUpsertDTOSchema.shape,
  visualizationType: z.literal('timeline'),
});

export const newUpsertTableLayoutDTOSchema = newBaseLayoutUpsertDTOSchema.extend({
  visualizationType: z.literal('table'),
  columns: z.array(newTableColumnDTOSchema).optional(),
  sort: SortSchema.optional(),
});

export type NewTableLayoutUpsertDTO = z.infer<typeof newUpsertTableLayoutDTOSchema>;

export const newLayoutUpsertDTOSchema = z
  .discriminatedUnion('visualizationType', [
    newUpsertSwimlaneLayoutDTOSchema,
    newUpsertTimelineLayoutDTOSchema,
    newUpsertTableLayoutDTOSchema,
  ])
  .superRefine((data, ctx) => {
    // Prevent duplicate header selections - header1 and header2 must be different
    // Only validate for canvas layouts (swimlane/timeline)
    if (['swimlane', 'timeline'].includes(data.visualizationType)) {
      const canvasData = data as NewCanvasLayoutUpsertDTO;
      if (canvasData.header1 && canvasData.header1 === canvasData.header2) {
        ctx.addIssue({
          code: 'custom',
          message: 'Header 1 and Header 2 cannot be the same',
          path: ['header2'],
        });
      }
    }
  });

export type NewLayoutUpsertDTO = z.infer<typeof newLayoutUpsertDTOSchema>;

export const newHeaderOrderDTOSchema = z.object({
  headerFieldId: newLayoutHeaderDTOSchema,
  orderedHeaderIds: z.array(z.string()),
});

export type NewHeaderOrderDTO = z.infer<typeof newHeaderOrderDTOSchema>;

export const newHeaderOrderUpsertDTOSchema = z.object({
  orderedHeaderIds: z.array(z.string()),
});

export type NewHeaderOrderUpsertDTO = z.infer<typeof newHeaderOrderUpsertDTOSchema>;

export const newHeaderHiddenDTOSchema = z.object({
  headerFieldId: newLayoutHeaderDTOSchema,
  hiddenHeaderIds: z.array(z.string()),
});

export const newHeaderHiddenUpsertDTOSchema = z.object({
  headerId: z.string(),
});

export type NewHeaderHiddenDTO = z.infer<typeof newHeaderHiddenDTOSchema>;

export const newHeaderConfigDTOSchema = newHeaderOrderDTOSchema.extend(
  newHeaderHiddenDTOSchema.shape,
);
export type NewHeaderConfigDTO = z.infer<typeof newHeaderConfigDTOSchema>;
